import { useEffect, useLayoutEffect, useState, type CSSProperties } from 'react';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { useUIStore } from '../../stores/uiStore';
import { TUTORIAL_STEPS } from '../../onboarding/tutorialSteps';
import { formatShortcutKeys } from '../../onboarding/shortcutCatalog';
import type { ThemePalette } from '../../types';

interface TutorialCoachProps {
  palette: ThemePalette;
  glass: CSSProperties;
  isViewOnly: boolean;
}

interface TargetBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

const BUBBLE_W = 320;
const BUBBLE_H_EST = 210;
const SPOT_PAD = 8;

function measureTarget(id: string | null): TargetBox | null {
  if (!id) return null;
  const el = document.querySelector(`[data-tour="${id}"]`) as HTMLElement | null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 && r.height < 2) return null;
  // Full-viewport targets (the canvas) make a useless spotlight — treat as untargeted.
  if (r.width > window.innerWidth * 0.85 && r.height > window.innerHeight * 0.85) return null;
  return { x: r.left, y: r.top, w: r.width, h: r.height };
}

function placeBubble(target: TargetBox | null, vw: number, vh: number): { left: number; top: number; arrow: 'left' | 'right' | 'top' | 'bottom' | 'none' } {
  if (!target) {
    return { left: Math.max(16, (vw - BUBBLE_W) / 2), top: Math.max(16, vh * 0.28), arrow: 'none' };
  }
  const candidates: Array<{ left: number; top: number; arrow: 'left' | 'right' | 'top' | 'bottom'; score: number }> = [
    { left: target.x + target.w + 18, top: target.y, arrow: 'left', score: vw - (target.x + target.w + 18 + BUBBLE_W) },
    { left: target.x - BUBBLE_W - 18, top: target.y, arrow: 'right', score: target.x - BUBBLE_W - 18 },
    { left: target.x, top: target.y + target.h + 18, arrow: 'top', score: vh - (target.y + target.h + 18 + BUBBLE_H_EST) },
    { left: target.x, top: target.y - BUBBLE_H_EST - 18, arrow: 'bottom', score: target.y - BUBBLE_H_EST - 18 },
  ];
  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0];
  return {
    left: Math.min(Math.max(12, best.left), vw - BUBBLE_W - 12),
    top: Math.min(Math.max(12, best.top), vh - BUBBLE_H_EST - 12),
    arrow: best.arrow,
  };
}

export function TutorialCoach({ palette, glass, isViewOnly }: TutorialCoachProps) {
  const status = useOnboardingStore(s => s.tutorialStatus);
  const stepIndex = useOnboardingStore(s => s.tutorialStep);
  const startTutorial = useOnboardingStore(s => s.startTutorial);
  const nextTutorialStep = useOnboardingStore(s => s.nextTutorialStep);
  const prevTutorialStep = useOnboardingStore(s => s.prevTutorialStep);
  const skipTutorial = useOnboardingStore(s => s.skipTutorial);
  const completeTutorial = useOnboardingStore(s => s.completeTutorial);

  const [target, setTarget] = useState<TargetBox | null>(null);
  const [tick, setTick] = useState(0);

  const step = TUTORIAL_STEPS[stepIndex] ?? TUTORIAL_STEPS[0];
  const active = !isViewOnly && status === 'active';

  useEffect(() => {
    if (isViewOnly || status !== 'pending') return;
    const t = window.setTimeout(() => startTutorial(), 900);
    return () => window.clearTimeout(t);
  }, [isViewOnly, status, startTutorial]);

  useEffect(() => {
    if (!active || !step) return;
    const ui = useUIStore.getState();
    ui.setToolboxOpen(true);
    ui.setDistractionFree(false);
    if (step.expandToolbar) ui.setToolbarExpanded(true);
    if (step.sidebarTab) ui.setSidebarTab(step.sidebarTab);
    ui.setOpenDropdown(step.openDropdown ?? null);
  }, [active, step]);

  useLayoutEffect(() => {
    if (!active) return;
    const update = () => setTarget(measureTarget(step.target));
    update();
    const id = window.setInterval(update, 240);
    window.addEventListener('resize', update);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', update);
    };
  }, [active, step, tick]);

  useEffect(() => {
    if (!active) return;
    const t = window.setTimeout(() => setTick(n => n + 1), 50);
    return () => window.clearTimeout(t);
  }, [active, stepIndex]);

  if (!active || !step) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pos = placeBubble(target, vw, vh);
  const isLast = stepIndex >= TUTORIAL_STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[560] pointer-events-none" data-tutorial-active={step.id}>
      {target && (
        <div
          className="tutorial-spotlight pointer-events-none"
          style={{
            left: target.x - SPOT_PAD,
            top: target.y - SPOT_PAD,
            width: target.w + SPOT_PAD * 2,
            height: target.h + SPOT_PAD * 2,
          }}
        />
      )}

      <div
        className="pointer-events-auto absolute tutorial-bubble-in z-[2]"
        style={{ left: pos.left, top: pos.top, width: BUBBLE_W }}
      >
        {pos.arrow !== 'none' && <span className={`tutorial-arrow tutorial-arrow-${pos.arrow}`} />}
        <div className="rounded-2xl border shadow-2xl backdrop-blur-md overflow-hidden" style={glass}>
          <div className="px-4 pt-3.5 pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-semibold leading-snug" style={{ color: palette.panelText }}>
                {step.title}
              </div>
              <span className="text-[10px] tabular-nums flex-shrink-0 mt-0.5" style={{ color: palette.panelSubText }}>
                {stepIndex + 1}/{TUTORIAL_STEPS.length}
              </span>
            </div>
            <p className="text-[11px] mt-1.5 leading-relaxed" style={{ color: palette.panelSubText }}>
              {step.body}
            </p>
            {step.keys && step.keys.length > 0 && (
              <div className="mt-2.5 flex flex-wrap items-center gap-1">
                {step.keys.map((key, i) => (
                  <span key={`${key}-${i}`} className="inline-flex items-center gap-1">
                    {step.chord && i > 0 && (
                      <span className="text-[9px]" style={{ color: palette.panelSubText }}>then</span>
                    )}
                    <kbd
                      className="px-1.5 py-0.5 rounded text-[10px] font-mono border"
                      style={{ borderColor: palette.panelBorder, color: palette.panelText }}
                    >
                      {formatShortcutKeys([key], false)}
                    </kbd>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div
            className="px-3 py-2.5 flex items-center justify-between border-t"
            style={{ borderColor: palette.panelBorder }}
          >
            <button
              type="button"
              className="text-[11px] px-2 py-1 rounded-lg hover:bg-white/5"
              style={{ color: palette.panelSubText }}
              onClick={skipTutorial}
            >
              Skip tour
            </button>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                className="text-[11px] px-2.5 py-1 rounded-lg hover:bg-white/5 disabled:opacity-30"
                style={{ color: palette.panelText }}
                disabled={stepIndex === 0}
                onClick={prevTutorialStep}
              >
                Back
              </button>
              <button
                type="button"
                className="text-[11px] px-3 py-1 rounded-lg font-semibold text-white"
                style={{ background: '#4f46e5' }}
                onClick={isLast ? completeTutorial : nextTutorialStep}
              >
                {isLast ? 'Done' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
